import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { RichTextHtml } from "../../../../cms/components/RichTextHtml";
import type {
  HeroBlockAction,
  HeroBlockImage,
} from "../../../../cms/components/blocks/HeroBlock/HeroBlock.props";
import type { IHeroSplitImageProps } from "./HeroSplitImage.props";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const isExternalBlank = (target?: string) => target === "_blank";

const HeroActions = ({ buttons }: { buttons?: HeroBlockAction[] | null }) => {
  const visibleActions = buttons ?? [];

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-3 max-[420px]:w-full">
      {visibleActions.map((action, index) => {
        const label = action.text;
        const isPrimary = action.style === "primary" || (!action.style && index === 0);
        const isLink = action.style === "link";

        return (
          <Link
            key={`${label}-${action.href}`}
            href={action.href}
            target={action.target}
            rel={isExternalBlank(action.target) ? "noopener noreferrer" : undefined}
            aria-label={action.ariaLabel || label}
            className={cn(
              "inline-flex min-h-11 items-center justify-center rounded-full text-sm font-bold transition-[background-color,border-color,box-shadow,transform] duration-200 motion-reduce:transition-none max-[420px]:w-full",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF]",
              isPrimary &&
                "bg-[#2144FF] px-5 py-3 text-white shadow-[0_14px_34px_rgba(33,68,255,0.25)] hover:bg-[#132EDB] active:bg-[#0F25B8] motion-safe:hover:-translate-y-0.5",
              !isPrimary &&
                !isLink &&
                "border border-[#D9E1FF] bg-white/90 px-5 py-3 text-[#2144FF] shadow-[0_12px_30px_rgba(33,68,255,0.08)] hover:border-[#2144FF] hover:bg-[#F6F1FF] active:border-[#132EDB] active:text-[#132EDB]",
              isLink && "px-0 py-2 text-[#2144FF] underline-offset-4 hover:underline",
            )}
          >
            {label}
            {!isLink ? (
              <span aria-hidden="true" className="ml-2">
                →
              </span>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
};

const HeroMediaFigure = ({ image, priority }: { image: HeroBlockImage; priority?: boolean }) => {
  const src = image.defaultSrc;

  if (!src) {
    return null;
  }

  const altText = image.alternateText;

  return (
    <figure className="group relative w-full min-w-0 lg:justify-self-end">
      <div className="overflow-hidden rounded-[2rem] border border-[#C9D6FF] bg-white p-2 shadow-[0_28px_80px_rgba(33,68,255,0.14)] sm:p-3">
        <div className="relative overflow-hidden rounded-[1.45rem] bg-[#FAFBFF]">
          <Image
            src={src}
            alt={altText}
            width={image.width ?? undefined}
            height={image.height ?? undefined}
            sizes="(min-width: 1024px) 52vw, 100vw"
            aspectRatio="16/9"
            className="block w-full"
            imageClassName="h-full w-full object-cover transition-transform duration-500 ease-out motion-reduce:transition-none motion-safe:group-hover:scale-[1.015]"
            loading={priority ? "eager" : "lazy"}
            priority={priority}
            srcSet={image.srcSet ?? undefined}
            style={
              image.focalPoint
                ? { objectPosition: `${image.focalPoint.x}% ${image.focalPoint.y}%` }
                : undefined
            }
          />
        </div>
      </div>
      {image.caption ? (
        <figcaption className="px-3 pt-3 text-sm leading-6 text-[#4B5568]">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
};

const HeroCopy = ({ props }: { props: IHeroSplitImageProps }) => {
  return (
    <div className="flex max-w-[39rem] flex-col gap-5 sm:gap-6">
      {props.eyebrow || props.heading ? (
        <div>
          {props.eyebrow ? (
            <p
              className={cn(
                "mb-4 text-xs font-extrabold uppercase tracking-[0.18em]",
                "text-[#2144FF]",
              )}
            >
              {props.eyebrow}
            </p>
          ) : null}
          {props.heading ? (
            <h1
              className={cn(
                "text-balance text-[2.35rem] lg:text-[3.75rem] font-extrabold leading-[1.04] tracking-[-0.04em]",
                "text-[#151927]",
              )}
            >
              {props.heading}
            </h1>
          ) : null}
        </div>
      ) : null}
      {props.subheading ? (
        <RichTextHtml
          className={cn(
            "max-w-[34rem] text-pretty text-base leading-7 sm:text-lg sm:leading-8",
            "text-[#4B5568]",
          )}
          html={props.subheading}
        />
      ) : null}
      <HeroActions buttons={props.buttons} />
    </div>
  );
};

export const HeroSplitImage = (props: IHeroSplitImageProps) => {
  const panelImage = props.image ?? undefined;

  if (!panelImage?.defaultSrc) {
    return (
      <div className="max-w-[54rem] py-4 sm:py-8 lg:py-12">
        <HeroCopy props={props} />
      </div>
    );
  }

  return (
    <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(28rem,1.05fr)] lg:gap-14 xl:gap-16">
      <HeroCopy props={props} />
      <HeroMediaFigure image={panelImage} priority={props.priority ?? undefined} />
    </div>
  );
};

HeroSplitImage.displayName = "content::HeroSplitImage";
